import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGlobalSettingStore } from 'src/models/useGlobalSettingModel';
import { ServiceNameEnum } from '@Types/index';

import DlpExchangeOnlinePolicy from '../PolicyTemplate/Email/ExchangeOnline';
import { useOrgStore } from 'src/models/useOrgModel';
import { useAccountStore } from 'src/models/useAccountModel';

import DlpExchangeOnlineOutboundPolicy from '../PolicyTemplate/Email/ExchangeOnlineOutbound';
import DlpExchangeOnlineInboundPolicy from '../PolicyTemplate/Email/ExchangeOnlineInbound';
import DlpGmailPolicy from '../PolicyTemplate/Email/Gmail';

import DlpGmailInboundPolicy from '../PolicyTemplate/Email/GmailInlineInbound';
import DlpGmailOutboundPolicy from '../PolicyTemplate/Email/GmailInlineOutbound';
import DlpTeamsPolicy from '../PolicyTemplate/Collaboration/Teams';
import DlpTeamsChatPolicy from '../PolicyTemplate/Collaboration/TeamsChat';
import DlpOneDrivePolicy from '../PolicyTemplate/Collaboration/OneDrive';
import DlpSharePointPolicy from '../PolicyTemplate/Collaboration/SharePoint';
import DlpDropboxPolicy from '../PolicyTemplate/Collaboration/Dropbox';
import DlpBoxPolicy from '../PolicyTemplate/Collaboration/Box';
import DlpGoogleDrivePolicy from '../PolicyTemplate/Collaboration/GoogleDrive';
import DlpSalesforceSandbox from '../PolicyTemplate/Collaboration/SalesforceSandbox';
import DlpSalesforceProduction from '../PolicyTemplate/Collaboration/SalesforceProduction';
import { useLoginUserStore } from '@Models/useLoginUserModel';
import {
  getDlpConfig,
  getDlpExchangeTemplateConfig,
} from '@Policy/TemplateConfig/cfg.dlp.exchange';
import {
  ApplyBoundEnum,
  CollaborationPolicyNameEnum,
  MailPolicyNameEnum,
} from '@Policy/utils/types';
import { getDlpExchangeInboundTemplateConfig } from '@Policy/TemplateConfig/cfg.dlp.exchangeInbound';
import { getDlpExchangeOutboundTemplateConfig } from '@Policy/TemplateConfig/cfg.dlp.exchangeOutbound';
import { getDlpGmailTemplateConfig } from '@Policy/TemplateConfig/cfg.dlp.gmail';
import { getDlpOneDriveTemplateConfig } from '@Policy/TemplateConfig/cfg.dlp.oneDrive';
import { getDlpSharePointTemplateConfig } from '@Policy/TemplateConfig/cfg.dlp.sharePoint';
import { getDlpGmailInboundTemplateConfig } from '@Policy/TemplateConfig/cfg.dlp.gmailInbound';
import { getDlpGmailOutboundTemplateConfig } from '@Policy/TemplateConfig/cfg.dlp.gmailOutbound';
import { getDlpTeamsTemplateConfig } from '@Policy/TemplateConfig/cfg.dlp.teams';
import { getDlpTeamsChatTemplateConfig } from '@Policy/TemplateConfig/cfg.dlp.teamsChat';
import PageNotFound from '@Components/Common/PageNotFound';
import { getDlpDropboxTemplateConfig } from '@Policy/TemplateConfig/cfg.dropbox.dlp';
import { getDlpBoxTemplateConfig } from '@Policy/TemplateConfig/cfg.box.dlp';
import { getDlpGoogleDriveTemplateConfig } from '@Policy/TemplateConfig/cfg.googleDrive.dlp';
import { getDlpSalesforceSandboxTemplateConfig } from '@Policy/TemplateConfig/cfg.dlp.salesforceSandbox';
import { getDlpSalesforceProductionTemplateConfig } from '@Policy/TemplateConfig/cfg.dlp.salesforceProduction';

const Policies = () => {
  const { service } = useParams();
  const { t } = useTranslation();
  const loginUserModel = useLoginUserStore();
  const globalSettingModule = useGlobalSettingStore();
  const { orgId, switchOrgId } = useOrgStore();
  const { groupCompanyGuid } = useAccountStore();
  const { selectedOrgGlobalSetting } = globalSettingModule;
  const mipFeatureShow = !!selectedOrgGlobalSetting?.mipFeatureShow;

  const policyComponent = useMemo(() => {
    let templateData: any = {};
    let configData: any = {};
    let newOrgId = orgId;

    if (orgId === 'all') {
      switchOrgId(groupCompanyGuid);
      newOrgId = groupCompanyGuid;
    }

    switch (service) {
      case ServiceNameEnum.Exchange:
        templateData = getDlpExchangeTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        configData.exchangeOnlineApplyBoundOptions = [
          ApplyBoundEnum.sentMessageOnly,
          ApplyBoundEnum.incomming,
          ApplyBoundEnum.all,
        ];
        return (
          <DlpExchangeOnlinePolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      case MailPolicyNameEnum.ExchangeInlineInbound:
        templateData = getDlpExchangeInboundTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        return (
          <DlpExchangeOnlineInboundPolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      case MailPolicyNameEnum.ExchangeInlineOutbound:
        templateData = getDlpExchangeOutboundTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        return (
          <DlpExchangeOnlineOutboundPolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      case MailPolicyNameEnum.Gmail:
        templateData = getDlpGmailTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        return (
          <DlpGmailPolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      case CollaborationPolicyNameEnum.OneDrive:
        templateData = getDlpOneDriveTemplateConfig(
          t,
          loginUserModel.user?.accountEmail,
          mipFeatureShow
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        return (
          <DlpOneDrivePolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      case CollaborationPolicyNameEnum.SharePoint:
        templateData = getDlpSharePointTemplateConfig(
          t,
          loginUserModel.user?.accountEmail,
          mipFeatureShow
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        return (
          <DlpSharePointPolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      case MailPolicyNameEnum.GmailInlineInbound:
        templateData = getDlpGmailInboundTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        return (
          <DlpGmailInboundPolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      case MailPolicyNameEnum.GmailInlineOutbound:
        templateData = getDlpGmailOutboundTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        return (
          <DlpGmailOutboundPolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      case CollaborationPolicyNameEnum.Teams:
        templateData = getDlpTeamsTemplateConfig(
          t,
          loginUserModel.user?.accountEmail,
          mipFeatureShow
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        return (
          <DlpTeamsPolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      case CollaborationPolicyNameEnum.TeamsChat:
        templateData = getDlpTeamsChatTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        return (
          <DlpTeamsChatPolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      case CollaborationPolicyNameEnum.GoogleDrive:
        templateData = getDlpGoogleDriveTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        return (
          <DlpGoogleDrivePolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      case CollaborationPolicyNameEnum.Dropbox:
        templateData = getDlpDropboxTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        return (
          <DlpDropboxPolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      case CollaborationPolicyNameEnum.Box:
        templateData = getDlpBoxTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        return (
          <DlpBoxPolicy
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      case CollaborationPolicyNameEnum.SalesforceSandbox:
        templateData = getDlpSalesforceSandboxTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        return (
          <DlpSalesforceSandbox
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      case CollaborationPolicyNameEnum.SalesforceProduction:
        templateData = getDlpSalesforceProductionTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, templateData);
        return (
          <DlpSalesforceProduction
            template={templateData}
            config={configData}
            orgId={newOrgId}
          />
        );
        break;
      default:
        return <PageNotFound />;
        break;
    }
  }, [
    groupCompanyGuid,
    loginUserModel.user?.accountEmail,
    mipFeatureShow,
    orgId,
    selectedOrgGlobalSetting,
    service,
    switchOrgId,
    t,
  ]);

  return <>{policyComponent}</>;
};
export default Policies;
